import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LinkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useEmployersService } from '@restworld/data-services';

import ShareLinkModal from './share-link-modal';

export function ReferralStickyCTA() {
  const { t } = useTranslation('referral-page');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const employersService = useEmployersService();

  const { data: referralData } = useQuery({
    queryKey: ['referral-link'],
    // Unwraps inside the queryFn, not through `select`: `select` runs per observer
    // and never touches the cache, so it would store the AxiosResponse under a key
    // every other consumer reads as the payload. See available-credits.tsx.
    queryFn: async () => (await employersService.getReferralLink()).data,
  });

  const referralLink = referralData?.referral_link ?? '';

  return (
    <StickyWrapper>
      <CTACard>
        <CTATitle>{t('want_to_search_free')}</CTATitle>
        <CTAButtonWrapper>
          <Button variant="secondary-reversed" StartIcon={LinkIcon} fullwidth onClick={() => setIsShareModalOpen(true)}>
            {t('share_your_link')}
          </Button>
        </CTAButtonWrapper>
      </CTACard>

      <ShareLinkModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        referralLink={referralLink}
        source="sticky_cta"
      />
    </StickyWrapper>
  );
}

const StickyWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('sticky bottom-4 z-20 flex justify-center px-4 pointer-events-none', className)} {...props} />
);

const CTACard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col items-center gap-3 w-full max-w-[640px] p-6 rounded-2xl pointer-events-auto',
      className
    )}
    style={{
      background: 'radial-gradient(circle at 100% 0%, rgba(72, 36, 133, 1) 0%, rgba(28, 28, 51, 1) 100%)',
      boxShadow: '0px 4px 32px 0px rgba(102, 51, 187, 0.5)',
    }}
    {...props}
  />
);

const CTATitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-sm font-medium text-white text-center leading-[1.21]', className)} {...props} />
);

const CTAButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full', className)}
    style={{
      background: 'radial-gradient(circle at 0% 1%, rgba(72, 36, 133, 1) 0%, rgba(240, 235, 248, 0.1) 100%)',
      boxShadow: '0px 0px 24px 0px rgba(102, 51, 187, 0.75)',
      borderRadius: '8px',
    }}
    {...props}
  />
);
